import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Eye, CheckCircle, XCircle, Trash2, FileText, Search, RefreshCw, Clock, IndianRupee } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';
import AddEditSalesReturnModal from '../../components/modals/AddEditSalesReturnModal';

const SalesReturnsPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [returns, setReturns] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, refunded: 0, total_value: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchReturns();
    }
  }, [bid, statusFilter]);

  const fetchReturns = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter.toUpperCase());
      }

      const response = await api.get(`/sales-returns?${params}`);
      const data = response.data;
      setReturns(data);
      const calcStats = {
        total: data.length,
        pending: data.filter(r => r.status === 'PENDING').length,
        approved: data.filter(r => r.status === 'APPROVED').length,
        refunded: data.filter(r => r.status === 'REFUNDED').length,
        total_value: data.reduce((sum, r) => sum + (r.grand_total || 0), 0)
      };
      setStats(calcStats);
    } catch (error) {
      console.error('Error fetching sales returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReturns = returns.filter(ret =>
    ret.return_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ret.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    navigate(`/business/${bid}/sales/returns/new`);
  };

  const handleEdit = (ret) => {
    if (ret.status !== 'PENDING') {
      toast.error('Only PENDING returns can be edited');
      return;
    }
    setSelectedReturn(ret);
    setShowModal(true);
  };

  const handleView = (ret) => {
    navigate(`/business/${bid}/sales/returns/${ret.id}`);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedReturn(null);
  };

  const handleSuccess = () => {
    fetchReturns();
    handleModalClose();
  };

  const handleStatusChange = async (returnId, newStatus) => {
    if (!window.confirm(`${newStatus} this sales return?`)) return;

    try {
      await api.patch(`/sales-returns/${returnId}/status?status=${newStatus}`);
      fetchReturns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (returnId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this sales return? This action cannot be undone.')) return;

    try {
      await api.delete(`/sales-returns/${returnId}`);
      fetchReturns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete return');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
      APPROVED: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      REFUNDED: { bg: 'bg-blue-100', text: 'text-blue-700', icon: RefreshCw }
    };
    const badge = badges[status] || badges.PENDING;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageSlideIn">
      {/* ════════════════════════════════════════════════════════════════════════
          PREMIUM HEADER - SINGLE ROW: KPI + Search + Status + CTA
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="
        relative overflow-hidden flex-shrink-0
        bg-gradient-to-br from-slate-50/95 via-emerald-50/40 to-teal-50/30
        backdrop-blur-sm
        border-b border-white/60
        shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)]
        rounded-b-2xl
      ">
        {/* Subtle glass overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-white/20 to-transparent pointer-events-none" />
        
        {/* Inner glow line at top */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent" />
        
        <div className="relative z-10 px-6 py-4">
          {/* Title Row */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-[17px] font-medium text-slate-700 tracking-tight">Sales Returns</h1>
              <p className="text-[11px] text-slate-500/80 mt-0.5 font-normal">Manage product returns and refunds</p>
            </div>
          </div>

          {/* SINGLE ROW: Stats Pills + Search + Status Filter + CTA Button */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Stats Pills */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 border border-white/80 rounded-lg backdrop-blur-sm shadow-sm">
              <FileText className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.5} />
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wider text-slate-400 font-medium leading-none">Total</span>
                <span className="text-[13px] font-semibold text-slate-700 leading-tight">{stats.total}</span>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50/60 border border-amber-200/30 rounded-lg backdrop-blur-sm shadow-sm">
              <Clock className="w-3.5 h-3.5 text-amber-500" strokeWidth={1.5} />
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wider text-slate-400 font-medium leading-none">Pending</span>
                <span className="text-[13px] font-semibold text-amber-600 leading-tight">{stats.pending}</span>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50/60 border border-emerald-200/30 rounded-lg backdrop-blur-sm shadow-sm">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500" strokeWidth={1.5} />
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wider text-slate-400 font-medium leading-none">Approved</span>
                <span className="text-[13px] font-semibold text-emerald-600 leading-tight">{stats.approved}</span>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50/60 border border-blue-200/30 rounded-lg backdrop-blur-sm shadow-sm">
              <RefreshCw className="w-3.5 h-3.5 text-blue-500" strokeWidth={1.5} />
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wider text-slate-400 font-medium leading-none">Refunded</span>
                <span className="text-[13px] font-semibold text-blue-600 leading-tight">{stats.refunded}</span>
              </div>
            </div>
            
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-rose-50/60 border border-rose-200/30 rounded-lg backdrop-blur-sm shadow-sm">
              <IndianRupee className="w-3.5 h-3.5 text-rose-500" strokeWidth={1.5} />
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-wider text-slate-400 font-medium leading-none">Total Value</span>
                <span className="text-[13px] font-semibold text-rose-600 leading-tight">{formatCompactCurrency(stats.total_value)}</span>
              </div>
            </div>

            {/* Flexible spacer */}
            <div className="flex-1" />

            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by return number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-[280px] pl-10 pr-4 py-2 bg-white/70 border border-slate-200/50 rounded-lg text-[13px] focus:border-emerald-400 focus:ring-0 focus:outline-none transition-all placeholder:text-slate-400 shadow-sm"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-white/70 border border-slate-200/50 rounded-lg text-[12px] text-slate-600 shadow-sm focus:ring-0 focus:outline-none focus:border-emerald-400"
            >
              <option value="all">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="REFUNDED">Refunded</option>
            </select>

            {/* CTA Button */}
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-[12px] font-medium shadow-sm"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              New Sales Return
            </button>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="flex-1 overflow-hidden px-6 pb-5">
        <div className="max-w-[1600px] mx-auto h-full flex flex-col">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 flex-1 flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-10 h-10 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-[14px] text-slate-500">Loading returns...</p>
                </div>
              </div>
            ) : filteredReturns.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <RefreshCw className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-[16px] font-medium text-slate-600 mb-1">No sales returns found</p>
                  <p className="text-[13px] text-slate-400 mb-4">Record your first return to get started</p>
                  <button
                    onClick={handleAdd}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all duration-200 text-[13px] font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Create Sales Return
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Table Header */}
                <div className="flex-shrink-0 overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="tl-th tl-th-teal">Return #</th>
                        <th className="tl-th tl-th-teal">Customer</th>
                        <th className="tl-th tl-th-teal">Date</th>
                        <th className="tl-th tl-th-teal">Reason</th>
                        <th className="tl-th tl-th-teal">Status</th>
                        <th className="tl-th tl-th-teal tl-th-right">Amount</th>
                        <th className="tl-th tl-th-teal text-center">Actions</th>
                      </tr>
                    </thead>
                  </table>
                </div>

                {/* Table Body */}
                <div className="flex-1 overflow-y-auto overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <tbody className="divide-y divide-slate-100">
                      {filteredReturns.map((ret, index) => (
                        <tr
                          key={ret.id}
                          className="tl-tr tl-tr-hover cursor-pointer transition-colors duration-150"
                          onClick={() => handleView(ret)}
                          style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                        >
                          <td className="tl-td">
                            <p className="text-[14px] font-semibold text-teal-600">{ret.return_number}</p>
                            {ret.invoice_number && (
                              <p className="text-[11px] text-slate-500 mt-0.5">Invoice: {ret.invoice_number}</p>
                            )}
                          </td>
                          <td className="tl-td">
                            <p className="text-[14px] font-medium text-slate-800">{ret.customer_name || '—'}</p>
                            {ret.customer_phone && (
                              <p className="text-[12px] text-slate-500 mt-0.5">{ret.customer_phone}</p>
                            )}
                          </td>
                          <td className="tl-td">
                            <p className="text-[13px] text-slate-600">{formatDate(ret.return_date)}</p>
                          </td>
                          <td className="tl-td">
                            <p className="text-[13px] text-slate-600 max-w-[200px] truncate">{ret.return_reason || '—'}</p>
                          </td>
                          <td className="tl-td">
                            {getStatusBadge(ret.status)}
                          </td>
                          <td className="tl-td tl-td-right">
                            <p className="text-[14px] font-semibold text-red-600">{formatCurrency(ret.grand_total)}</p>
                          </td>
                          <td className="tl-td">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleView(ret); }}
                                className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all duration-200"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {ret.status === 'PENDING' && (
                                <>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleEdit(ret); }}
                                    className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all duration-200"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleStatusChange(ret.id, 'APPROVED'); }}
                                    className="p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-200"
                                    title="Approve"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={(e) => handleDelete(ret.id, e)}
                                    className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              {ret.status === 'APPROVED' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleStatusChange(ret.id, 'REFUNDED'); }}
                                  className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                                  title="Process Refund"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <AddEditSalesReturnModal
          isOpen={showModal}
          onClose={handleModalClose}
          salesReturn={selectedReturn}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default SalesReturnsPage;
